import { type ItemOf } from '@shared/board'
import { clsx } from 'clsx'
import { BookOpen } from 'lucide-react'
import { useI18n } from '@/i18n/i18n'
import { useMaterial, useRegionCanvas } from '@/materials/hooks'
import { DRAG_HANDLE, NO_DRAG } from '../classes'
import { useBoardCtx } from '../context'

const FULL_PAGE = { x: 0, y: 0, w: 1, h: 1 }
const THUMB_W = 44

/** A material on the board: its first page, its name and length; opens the reader. */
export function MaterialView({ item, selected }: { item: ItemOf<'material'>; selected: boolean }) {
  const { t } = useI18n()
  const { reader } = useBoardCtx()
  const material = useMaterial(item.data.materialId)
  const thumb = useRegionCanvas(material, 1, FULL_PAGE, THUMB_W)
  const first = material?.pages[0]
  const open = () => {
    reader.open(item.data.materialId)
  }
  return (
    <div
      className={clsx(
        'moot-node ' +
          DRAG_HANDLE +
          ' relative flex cursor-grab items-center gap-3 rounded-md border bg-surface px-3 py-2.5 shadow-sm active:cursor-grabbing',
        selected ? 'border-(--focus)' : 'border-line',
      )}
      style={{ width: item.w }}
      onDoubleClick={open}
    >
      <span
        className="flex-none overflow-hidden rounded-[3px] border border-line bg-white"
        style={{ width: THUMB_W, height: first ? Math.round((THUMB_W * first.h) / first.w) : 56 }}
      >
        <canvas ref={thumb} aria-hidden className="block size-full" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium">{item.data.name}</span>
        <span className="block text-[11.5px] text-muted">
          {material ? t('material.pages', { n: material.pages.length }) : t('material.missing')}
        </span>
      </span>
      <button
        type="button"
        onClick={open}
        aria-label={t('material.open')}
        title={t('material.open')}
        className={
          NO_DRAG +
          ' grid size-7 flex-none place-items-center rounded-sm text-muted hover:bg-surface-2 hover:text-ink'
        }
      >
        <BookOpen size={14} />
      </button>
    </div>
  )
}
